import {
    differenceInCalendarDays,
    formatISO,
    parseISO,
    startOfDay,
    subDays,
} from "date-fns";
import { getSupabaseClient } from "./supabaseClient";

export const normalizeHistory = (history = []) =>
    history
        .filter((item) => item?.puzzleId && item?.puzzleDate)
        .map((item) => ({
            ...item,
            puzzleDate: item.puzzleDate,
            sport: item.sport ?? "nfl",
            result: item.result ?? "win",
            completedAt: item.completedAt ?? formatISO(new Date()),
        }))
        .sort((a, b) => (a.puzzleDate < b.puzzleDate ? 1 : -1));

export const computeStreak = (history = [], sport = null) => {
    let normalized = normalizeHistory(history);

    // Filter by sport if specified
    if (sport) {
        normalized = normalized.filter((entry) => entry.sport === sport);
    }

    if (!normalized.length) {
        return 0;
    }

    // Get today's date at start of day
    const today = startOfDay(new Date());
    const mostRecentEntry = normalized[0];
    const mostRecentDate = startOfDay(parseISO(mostRecentEntry.puzzleDate));

    // Check if the most recent entry is from today or yesterday
    // If more than 1 day has passed, streak is broken
    const daysSinceLastPlay = differenceInCalendarDays(today, mostRecentDate);
    if (daysSinceLastPlay > 1) {
        return 0;
    }

    let streak = 0;
    let expectedDate = mostRecentDate;

    for (let i = 0; i < normalized.length; i++) {
        const entry = normalized[i];

        // Only wins count toward streak
        if (entry.result !== "win") {
            break;
        }

        const entryDate = startOfDay(parseISO(entry.puzzleDate));
        const diff = differenceInCalendarDays(expectedDate, entryDate);

        // Entry must be on the expected date (consecutive days)
        if (diff !== 0) {
            break;
        }

        streak++;
        expectedDate = subDays(expectedDate, 1);
    }
    return streak;
};

export const computeStreaksBySport = (history = []) => {
    return {
        nfl: computeStreak(history, "nfl"),
        nba: computeStreak(history, "nba"),
        mlb: computeStreak(history, "mlb"),
    };
};

export const computeBestStreakBySport = (history = [], sport) => {
    let normalized = normalizeHistory(history);

    if (sport) {
        normalized = normalized.filter((entry) => entry.sport === sport);
    }

    let best = 0;
    let current = 0;
    let previousDate = null;

    // Sort by date ascending for best streak calculation
    const sorted = [...normalized].sort((a, b) =>
        a.puzzleDate < b.puzzleDate ? -1 : 1
    );

    sorted.forEach((entry) => {
        if (entry.result !== "win") {
            current = 0;
            previousDate = null;
            return;
        }
        const entryDate = startOfDay(parseISO(entry.puzzleDate));
        if (!previousDate) {
            current = 1;
        } else if (differenceInCalendarDays(entryDate, previousDate) === 1) {
            current = current + 1;
        } else {
            current = 1;
        }
        previousDate = entryDate;
        best = Math.max(best, current);
    });

    return best;
};

export const saveProgressToSupabase = async (user, payload) => {
    const supabase = getSupabaseClient();
    if (!user || !supabase) {
        return { error: null };
    }

    // Use the new user_puzzle_completions table for better data structure
    const { error } = await supabase.from("user_puzzle_completions").upsert(
        {
            user_id: user.id,
            puzzle_id: payload.puzzleId,
            completed: true,
            result: payload.result,
            mistakes: payload.mistakes,
            guesses: payload.guesses,
            completed_at: payload.completedAt,
        },
        {
            onConflict: "user_id,puzzle_id",
        }
    );

    return { error };
};

export const loadUserProgressFromSupabase = async (user, sport) => {
    const supabase = getSupabaseClient();
    if (!user || !supabase) {
        return { data: [], error: null };
    }

    const { data, error } = await supabase
        .from("user_puzzle_completions")
        .select(
            `
            id,
            puzzle_id,
            completed,
            result,
            mistakes,
            guesses,
            completed_at,
            puzzles (
                puzzle_date,
                league
            )
        `
        )
        .eq("user_id", user.id)
        .order("completed_at", { ascending: false });

    if (error) {
        return { data: [], error };
    }

    // Transform the data to match the expected format
    const normalized = data
        .map((item) => ({
            puzzleId: item.puzzle_id,
            puzzleDate: item.puzzles?.puzzle_date || null,
            sport: item.puzzles?.league || sport,
            result: item.result,
            mistakes: item.mistakes,
            guesses: item.guesses,
            completedAt: item.completed_at,
        }))
        .filter((item) => item.puzzleDate);

    return { data: normalized, error: null };
};
